import React, { useCallback, useMemo, useRef, useState } from "react";

export interface DropzoneOptions {
    onDrop: (files: File[]) => void;
    accept: string;
    multiple: boolean;
    maxFiles: number | null;
    disabled: boolean;
    noClick: boolean;
}

export interface DropzoneRootProps {
    ref: React.RefObject<HTMLDivElement>;
    onClick: (event: React.MouseEvent<HTMLElement>) => void;
    onDragEnter: (event: React.DragEvent<HTMLElement>) => void;
    onDragOver: (event: React.DragEvent<HTMLElement>) => void;
    onDragLeave: (event: React.DragEvent<HTMLElement>) => void;
    onDrop: (event: React.DragEvent<HTMLElement>) => void;
}

export interface DropzoneInputProps {
    ref: React.RefObject<HTMLInputElement>;
    type: "file";
    accept: string;
    multiple: boolean;
    disabled: boolean;
    tabIndex: -1;
    autoComplete: "off";
    style: React.CSSProperties;
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    onClick: (event: React.MouseEvent<HTMLInputElement>) => void;
}

export interface DropzoneState {
    isDragActive: boolean;
    getRootProps: () => DropzoneRootProps;
    getInputProps: () => DropzoneInputProps;
}

const matchesOneAcceptRule = (file: File, rule: string): boolean => {
    if (rule === "*" || rule === "*/*") {
        return true;
    }
    if (rule.startsWith(".")) {
        return file.name.toLowerCase().endsWith(rule.toLowerCase());
    }
    const type = (file.type || "").toLowerCase();
    if (rule.endsWith("/*")) {
        return type.split("/")[0] === rule.slice(0, -2).toLowerCase();
    }
    return type === rule.toLowerCase();
};

const matchesAccept = (file: File, accept: string): boolean => {
    const rules = accept
        .split(",")
        .map(rule => rule.trim())
        .filter(rule => rule.length > 0);
    return rules.length === 0 || rules.some(rule => matchesOneAcceptRule(file, rule));
};

const isDirectoryItem = (item: DataTransferItem): boolean => {
    if (typeof item.webkitGetAsEntry !== "function") {
        return false;
    }
    const entry = item.webkitGetAsEntry();
    return entry !== null && entry.isDirectory;
};

const filesFromDataTransfer = (dataTransfer: DataTransfer | null): File[] => {
    if (!dataTransfer) {
        return [];
    }
    const files = Array.from(dataTransfer.files || []);
    const items = Array.from(dataTransfer.items || []);
    if (items.length !== files.length) {
        return files;
    }
    return files.filter((_, index) => !isDirectoryItem(items[index]));
};

const dragCarriesFiles = (dataTransfer: DataTransfer | null): boolean =>
    Array.from(dataTransfer?.types || []).includes("Files");

export const useDropzone = (options: DropzoneOptions): DropzoneState => {
    const { onDrop, accept, multiple, maxFiles, disabled, noClick } = options;
    const rootRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const dragTargets = useRef<EventTarget[]>([]);
    const [isDragActive, setIsDragActive] = useState(false);

    const deliver = useCallback(
        (candidates: File[]) => {
            const accepted = candidates.filter(file => matchesAccept(file, accept));
            const selected = multiple ? accepted : accepted.slice(0, 1);
            if (selected.length === 0) {
                return;
            }
            if (maxFiles !== null && selected.length > maxFiles) {
                return;
            }
            onDrop(selected);
        },
        [accept, multiple, maxFiles, onDrop],
    );

    const handleDragEnter = useCallback(
        (event: React.DragEvent<HTMLElement>) => {
            event.preventDefault();
            event.stopPropagation();
            if (disabled) {
                return;
            }
            dragTargets.current = [...dragTargets.current, event.target];
            if (dragCarriesFiles(event.dataTransfer)) {
                setIsDragActive(true);
            }
        },
        [disabled],
    );

    const handleDragOver = useCallback((event: React.DragEvent<HTMLElement>) => {
        event.preventDefault();
        event.stopPropagation();
    }, []);

    const handleDragLeave = useCallback((event: React.DragEvent<HTMLElement>) => {
        event.preventDefault();
        event.stopPropagation();
        dragTargets.current = dragTargets.current.filter(
            target => target !== event.target,
        );
        if (dragTargets.current.length === 0) {
            setIsDragActive(false);
        }
    }, []);

    const handleDrop = useCallback(
        (event: React.DragEvent<HTMLElement>) => {
            event.preventDefault();
            event.stopPropagation();
            dragTargets.current = [];
            setIsDragActive(false);
            if (disabled) {
                return;
            }
            deliver(filesFromDataTransfer(event.dataTransfer));
        },
        [disabled, deliver],
    );

    const handleRootClick = useCallback(
        (event: React.MouseEvent<HTMLElement>) => {
            if (disabled || noClick) {
                return;
            }
            event.stopPropagation();
            inputRef.current?.click();
        },
        [disabled, noClick],
    );

    const handleInputChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            const picked = Array.from(event.target.files || []);
            event.target.value = "";
            if (disabled) {
                return;
            }
            deliver(picked);
        },
        [disabled, deliver],
    );

    const handleInputClick = useCallback((event: React.MouseEvent<HTMLInputElement>) => {
        event.stopPropagation();
    }, []);

    const getRootProps = useCallback(
        (): DropzoneRootProps => ({
            ref: rootRef,
            onClick: handleRootClick,
            onDragEnter: handleDragEnter,
            onDragOver: handleDragOver,
            onDragLeave: handleDragLeave,
            onDrop: handleDrop,
        }),
        [handleRootClick, handleDragEnter, handleDragOver, handleDragLeave, handleDrop],
    );

    const getInputProps = useCallback(
        (): DropzoneInputProps => ({
            ref: inputRef,
            type: "file",
            accept,
            multiple,
            disabled,
            tabIndex: -1,
            autoComplete: "off",
            style: { display: "none" },
            onChange: handleInputChange,
            onClick: handleInputClick,
        }),
        [accept, multiple, disabled, handleInputChange, handleInputClick],
    );

    return useMemo(
        () => ({ isDragActive, getRootProps, getInputProps }),
        [isDragActive, getRootProps, getInputProps],
    );
};
